import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize,
  Download,
  History,
  MessageSquare,
} from 'lucide-react';

interface PDFControlsProps {
  pageNumber: number;
  numPages: number;
  scale: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitWidth: () => void;
  onDownload?: () => void;
  onHistoryClick?: () => void;
  onCommentsClick?: () => void;
  canDownload?: boolean;
  historyActive?: boolean;
  commentsActive?: boolean;
  commentCount?: number;
}

export function PDFControls({
  pageNumber,
  numPages,
  scale,
  onPrevPage,
  onNextPage,
  onZoomIn,
  onZoomOut,
  onFitWidth,
  onDownload,
  onHistoryClick,
  onCommentsClick,
  canDownload = false,
  historyActive = false,
  commentsActive = false,
  commentCount = 0,
}: PDFControlsProps) {
  return (
    <div className="flex min-h-[52px] items-center px-3 py-2 border-b border-editor-border bg-white">
      {/* Zoom controls - left */}
      <div className="flex items-center gap-1">
        <button
          onClick={onZoomOut}
          disabled={scale <= 0.5}
          aria-label="Zoom out"
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom out"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className="text-sm text-gray-600 min-w-[3rem] text-center">
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={onZoomIn}
          disabled={scale >= 3}
          aria-label="Zoom in"
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Zoom in"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          onClick={onFitWidth}
          aria-label="Fit width"
          className="p-1 rounded hover:bg-gray-100 ml-1"
          title="Fit width"
        >
          <Maximize className="h-4 w-4" />
        </button>
      </div>

      {/* Page navigation - center */}
      <div className="flex-1 flex items-center justify-center gap-1">
        <button
          onClick={onPrevPage}
          disabled={pageNumber <= 1}
          aria-label="Previous page"
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm text-gray-600 min-w-[4rem] text-center">
          {numPages > 0 ? `${pageNumber} / ${numPages}` : '-'}
        </span>
        <button
          onClick={onNextPage}
          disabled={pageNumber >= numPages}
          aria-label="Next page"
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Download & History - right */}
      <div className="flex items-center gap-1">
        {canDownload && onDownload && (
          <button
            onClick={onDownload}
            aria-label="Download PDF"
            title="Download PDF"
            className="p-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900"
          >
            <Download className="h-4 w-4" />
          </button>
        )}
        {onCommentsClick && (
          <button
            onClick={onCommentsClick}
            aria-label="Comments"
            title="Comments"
            className={`relative p-1 rounded hover:bg-gray-100 ${
              commentsActive ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            {commentCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {commentCount > 99 ? '99+' : commentCount}
              </span>
            )}
          </button>
        )}
        {onHistoryClick && (
          <button
            onClick={onHistoryClick}
            aria-label="Revision History"
            title="Revision History"
            className={`p-1 rounded hover:bg-gray-100 ${
              historyActive ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <History className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
